import { TractNRI, TractNRITable } from 'components/nri-table';
import { InlineNotification } from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { developmentAreaIdAtom, useDevelopmentAreaNriAtom } from './atoms';
// eslint-disable-next-line camelcase
import { unstable_batchedUpdates } from 'react-dom';
import { formatResource, mapSlotNRIResponseToTractNRI } from 'utils/misc';

export const NriTable = ({
  unitId,
  uwi,
  wellName,
  uat,
  onRowSelected,
  onTractsCollected,
  onSubtractsCollected,
}: {
  unitId: number;
  uwi: string | null;
  wellName: string | null;
  uat?: string | null;
  onRowSelected: (row: TractNRI | null) => void;
  onTractsCollected?: (
    tractGeojsons: GeoJSON.Feature<GeoJSON.Polygon>[]
  ) => void;
  onSubtractsCollected?: (
    subtractGeojsons: GeoJSON.Feature<GeoJSON.Polygon>[]
  ) => void;
}) => {
  const setDevelopmentAreaId = useSetAtom(developmentAreaIdAtom);

  useEffect(() => {
    setDevelopmentAreaId(unitId);
  }, [unitId, setDevelopmentAreaId]);

  const developmentAreaNri = useDevelopmentAreaNriAtom(unitId);
  const developmentAreaNriResource = useAtomValue(developmentAreaNri);
  const {
    data,
    loading: isLoading,
    error,
  } = formatResource(developmentAreaNriResource);

  const tractNRIRows = useMemo(() => {
    if (!data || 'error' in data) return [];
    const allRows = mapSlotNRIResponseToTractNRI(data);

    let filteredRows: TractNRI[] = [];
    if (uwi) {
      filteredRows = allRows.filter((row) => row.slotId === uwi);
    } else if (wellName) {
      filteredRows = allRows.filter((row) => row.wellName === wellName);
    }

    // Filter by selected UAT if provided
    if (uat && filteredRows.length > 0) {
      filteredRows = filteredRows.filter((row) =>
        row.subtracts?.some((subtract) => subtract.uat === uat)
      );
    }

    return filteredRows;
  }, [data, uwi, wellName, uat]);

  // Collect tract and subtract geojsons when data loads
  useEffect(() => {
    if (!isLoading && tractNRIRows.length > 0) {
      unstable_batchedUpdates(() => {
        if (onTractsCollected) {
          const tractGeojsons = tractNRIRows
            .filter((row) => row.geojson)
            .map((row) => row.geojson as GeoJSON.Feature<GeoJSON.Polygon>);

          onTractsCollected(tractGeojsons);
        }

        if (onSubtractsCollected) {
          const subtractGeojsons = tractNRIRows
            .flatMap((row) => row.subtracts ?? [])
            .filter((subtract) => subtract.geojson)
            .map(
              (subtract) => subtract.geojson as GeoJSON.Feature<GeoJSON.Polygon>
            );

          if (subtractGeojsons.length > 0) {
            onSubtractsCollected(subtractGeojsons);
          }
        }
      });
    }
  }, [isLoading, tractNRIRows, onTractsCollected, onSubtractsCollected]);

  return (
    <>
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error loading NRI data"
          subtitle={error}
          hideCloseButton={false}
        />
      )}
      <TractNRITable
        tractNRIRows={tractNRIRows}
        isLoading={isLoading}
        onRowSelected={onRowSelected}
      />
    </>
  );
};
